import React, { useEffect, useMemo } from 'react'
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, compareAsc } from 'date-fns'
import { useEventsStore } from '../../stores/eventsStore'
import { useUserPreferencesStore } from '../../stores/userPreferencesStore'
import { getColorScheme } from '../../utils/colorSchemes'
import { getTypeColor } from '../../utils/eventTypeColors'
import { usePermissions } from '../../hooks/usePermissions'
import { useFamilyStore } from '../../stores/familyStore'

interface DayEventsListProps {
  date: Date
  childId: string
  onAddEvent?: (defaultDate: Date) => void
  onEditEvent?: (eventId: string, defaultDate: Date) => void
}

export const DayEventsList: React.FC<DayEventsListProps> = ({ date, childId, onEditEvent }) => {
  const { events, fetchEvents, deleteEvent } = useEventsStore()
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const { isObserver } = usePermissions()
  const { family, members } = useFamilyStore()

  const startISO = useMemo(() => startOfDay(date).toISOString(), [date])
  const endISO = useMemo(() => endOfDay(date).toISOString(), [date])
  const monthStartISO = useMemo(() => startOfMonth(date).toISOString(), [date])
  const monthEndISO = useMemo(() => endOfMonth(date).toISOString(), [date])

  useEffect(() => {
    if (childId) fetchEvents(childId, monthStartISO, monthEndISO)
  }, [childId, monthStartISO, monthEndISO])

  const dayEvents = useMemo(() => {
    const typeOrder: Record<string, number> = { note: 0, expense: 1, event: 0 }
    return events
      .filter(ev => ev.child_id === childId && ev.start_at >= startISO && ev.start_at <= endISO)
      .filter(ev => !(ev.parents_only && isObserver))
      .sort((a, b) => {
        const t = (typeOrder[a.type as keyof typeof typeOrder] ?? 99) - (typeOrder[b.type as keyof typeof typeOrder] ?? 99)
        if (t !== 0) return t
        return compareAsc(new Date(a.start_at), new Date(b.start_at))
      })
  }, [events, childId, startISO, endISO])

  // Mapowanie user_id -> nazwa domu (np. "Mama", "Tata")
  const userIdToLabel = useMemo(() => {
    const map = new Map<string, string>()
    const homeAName = family?.home_a_name || 'Dom A'
    const homeBName = family?.home_b_name || 'Dom B'
    for (const m of members || []) {
      if (!m.user_id) continue
      const home = m.assigned_home
        || (m.role === 'parent_a' ? 'home_a' : m.role === 'parent_b' ? 'home_b' : null)
      if (home === 'home_a') map.set(m.user_id, homeAName)
      else if (home === 'home_b') map.set(m.user_id, homeBName)
    }
    return map
  }, [members, family])

  // Mapowanie user_id -> assigned_home (home_a / home_b)
  const userIdToHome = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of members || []) {
      if (!m.user_id) continue
      // Użyj assigned_home jeśli ustawione, inaczej fallback: parent_a→home_a, parent_b→home_b
      const home = m.assigned_home
        || (m.role === 'parent_a' ? 'home_a' : m.role === 'parent_b' ? 'home_b' : null)
      if (home) map.set(m.user_id, home)
    }
    return map
  }, [members])

  return (
    <div className="mt-2 space-y-2">
      {dayEvents.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-4">Brak notatek w wybranym dniu.</div>
      ) : (
        dayEvents.map(ev => {
          const createdByLabel = userIdToLabel.get(ev.created_by) || '—'
          return (
            <div key={ev.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 pl-4 hover:shadow-md transition-shadow" style={{ borderLeftWidth: 4, borderLeftColor: ev.type === 'expense' ? getTypeColor('expense', scheme.id) : (userIdToHome.get(ev.created_by) === 'home_b' ? scheme.homeB.color : scheme.homeA.color) }}>
              <div className="flex w-full items-stretch gap-3">
                {/* Lewa część: 4/5 szerokości */}
                <div className="w-4/5">
                  {/* Wiersz 1: Rodzaj, tytuł, kwota (dla kosztu) */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border text-white"
                      title={ev.type === 'event' ? 'note' : ev.type}
                      style={ev.type === 'expense'
                        ? { backgroundColor: getTypeColor('expense', scheme.id), borderColor: getTypeColor('expense', scheme.id) }
                        : { backgroundColor: userIdToHome.get(ev.created_by) === 'home_b' ? scheme.homeB.color : scheme.homeA.color, borderColor: userIdToHome.get(ev.created_by) === 'home_b' ? scheme.homeB.color : scheme.homeA.color }}
                    >
                      {ev.type === 'expense' ? 'Koszt' : 'Notatka'}
                    </span>
                    <span className="font-medium text-slate-800">{ev.title}</span>
                    {ev.type === 'expense' && ev.amount != null && (
                      <span className="ml-2 text-sm font-semibold text-slate-700">{Number(ev.amount).toFixed(2)} {ev.currency || 'PLN'}</span>
                    )}
                  </div>
                  {/* Wiersz 2: Czas i data */}
                  <div className="text-xs text-slate-700 mb-1">
                    {ev.all_day ? (
                      <>
                        <span className="font-semibold">Cały dzień</span>
                        {` • ${format(new Date(ev.start_at), 'yyyy-MM-dd')}`}
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">{`${format(new Date(ev.start_at), 'HH:mm')}${ev.end_at ? `–${format(new Date(ev.end_at), 'HH:mm')}` : ''}`}</span>
                        {` • ${format(new Date(ev.start_at), 'yyyy-MM-dd')}`}
                      </>
                    )}
                  </div>
                  {/* Wiersz 3: Miejsce (tylko dla notatki) */}
                  {ev.type !== 'expense' && ev.location ? (
                    <div className="text-xs text-slate-700 mb-1">Miejsce: <span className="font-semibold">{ev.location}</span></div>
                  ) : null}
                  {/* Wiersz 4: Notatka (jeśli jest) */}
                  {ev.notes ? (
                    <div className="text-xs font-semibold text-slate-700 mb-1">{ev.notes}</div>
                  ) : null}
                  {/* Wiersz 5: Widoczność */}
                  {ev.parents_only && (
                    <div className="flex items-center gap-1 text-xs text-amber-600 mb-1">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5"><path fillRule="evenodd" d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7H4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-.5V4.5A3.5 3.5 0 0 0 8 1Zm2 6V4.5a2 2 0 1 0-4 0V7h4Z" clipRule="evenodd" /></svg>
                      <span>Tylko dla rodziców</span>
                    </div>
                  )}
                  {/* Wiersz 6: Autor */}
                  <div className="text-xs text-slate-700">Utworzył/a: <span className="font-semibold">{createdByLabel}</span></div>
                </div>
                {/* Prawa część: 1/5 szerokości – przyciski pionowo */}
                {!isObserver && (
                  <div className="w-1/5 shrink-0 flex flex-col items-stretch justify-start gap-1.5">
                    {onEditEvent && (
                      <button onClick={() => onEditEvent(ev.id, new Date(ev.start_at))} className="px-3 py-1.5 text-xs font-medium rounded-full text-slate-600 hover:bg-slate-100 transition-colors">Edytuj</button>
                    )}
                    <button onClick={() => deleteEvent(ev.id)} className="px-3 py-1.5 text-xs font-medium rounded-full text-red-500 hover:bg-red-50 transition-colors">Usuń</button>
                  </div>
                )}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

export default DayEventsList


